import { getReportWorksData } from './mapPathUtils';

/**
 * Cores da legenda de valor dos mapas (createMapValueLegends).
 * A identidade visual multi-trabalho NÃO pode reutilizar estas cores.
 */
const MAP_LEGEND_COLOR_BLOCKLIST = new Set([
    '#ff0000',
    '#ff5733',
    '#ffca28',
    '#f4e538',
    '#36e236',
    '#118e11',
    '#1976d2',
].map((c) => c.toLowerCase()));

/**
 * Paleta de identidade por trabalho (bordas, gráficos, mapa de erros).
 * Escolhida para não coincidir com a legenda de valor dos mapas.
 */
const WORK_IDENTITY_COLOR_CANDIDATES = [
    '#7b1fa2',
    '#00838f',
    '#c2185b',
    '#4527a0',
    '#00695c',
    '#ad1457',
    '#283593',
    '#4e342e',
    '#37474f',
    '#6a1b9a',
    '#01579b',
    '#880e4f',
];

export const GRAPH_WORK_COLORS = WORK_IDENTITY_COLOR_CANDIDATES.filter(
    (color) => !MAP_LEGEND_COLOR_BLOCKLIST.has(String(color).toLowerCase())
);

export const getWorkColorByIndex = (index) => {
    const palette = GRAPH_WORK_COLORS.length > 0
        ? GRAPH_WORK_COLORS
        : WORK_IDENTITY_COLOR_CANDIDATES;
    const safeIndex = Math.abs(Number(index) || 0) % palette.length;
    return palette[safeIndex];
};

const isValidDateValue = (value) => {
    if (value === null || value === undefined || value === '') {
        return false;
    }
    const date = new Date(value);
    return !isNaN(date.getTime()) && date.getTime() !== 0;
};

export const toDayString = (value) => {
    if (!isValidDateValue(value)) {
        return null;
    }
    return new Date(value).toDateString();
};

export const getWorkSeriesMeta = (workData, index) => {
    const id = workData?.trabalhoId ?? workData?.id ?? index;
    const label = workData?.label || `#${id}`;
    return {
        id,
        key: `work_${id}`,
        label,
        color: workData?.color || getWorkColorByIndex(index),
    };
};

/**
 * União dos dias presentes em todos os trabalhos selecionados.
 */
export const extractUniqueDaysFromReportData = (data, getSourceItems, dateField) => {
    const daysSet = new Set();
    for (const workData of getReportWorksData(data)) {
        const items = getSourceItems(workData);
        if (!Array.isArray(items)) {
            continue;
        }
        for (const item of items) {
            const day = toDayString(item?.[dateField]);
            if (day) {
                daysSet.add(day);
            }
        }
    }
    return Array.from(daysSet).sort((a, b) => new Date(a) - new Date(b));
};

const computeYDomain = (values) => {
    if (!values.length) {
        return ['auto', 'auto'];
    }
    let min = Math.min(...values);
    let max = Math.max(...values);
    if (!Number.isFinite(min) || !Number.isFinite(max)) {
        return ['auto', 'auto'];
    }
    if (min === max) {
        const pad = Math.abs(min) * 0.05 || 1;
        return [min - pad, max + pad];
    }
    const pad = (max - min) * 0.05;
    return [min - pad, max + pad];
};

/**
 * Monta séries (uma linha por trabalho) para o dia selecionado.
 * O eixo Y cobre o min/max global dos trabalhos no dia.
 */
export const buildMultiWorkChartData = ({
    data,
    selectedDay,
    getSourceItems,
    dateField,
    getValue,
}) => {
    const works = getReportWorksData(data);
    const series = [];
    const byTimestamp = new Map();
    const numericValues = [];

    works.forEach((workData, index) => {
        const meta = getWorkSeriesMeta(workData, index);
        const items = getSourceItems(workData);
        if (!Array.isArray(items) || items.length === 0) {
            return;
        }

        let hasValue = false;
        for (const item of items) {
            const rawDate = item?.[dateField];
            if (!isValidDateValue(rawDate)) {
                continue;
            }
            const date = new Date(rawDate);
            if (date.toDateString() !== selectedDay) {
                continue;
            }
            const value = getValue(item, workData);
            if (value === null || value === undefined || Number.isNaN(Number(value))) {
                continue;
            }
            const numeric = Number(value);
            const ts = date.getTime();
            if (!byTimestamp.has(ts)) {
                byTimestamp.set(ts, {
                    name: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                    _ts: ts,
                });
            }
            byTimestamp.get(ts)[meta.key] = numeric;
            numericValues.push(numeric);
            hasValue = true;
        }

        if (hasValue) {
            series.push(meta);
        }
    });

    const chartData = Array.from(byTimestamp.values()).sort((a, b) => a._ts - b._ts);

    return {
        chartData,
        series,
        yDomain: computeYDomain(numericValues),
    };
};
